<script>
import StrFormula from "@/views/PaySection/Components/StrFormula.vue";
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import {usePaySectionStore} from "@/stores/PaySection";

export default {
    name: "PaySectionTable",
    components: {StrFormula},
    props: {
        paySections: Array,
        loading: Boolean,
        search: String,
        showAddAction: Boolean,
        showUpdateAction: Boolean,
        showDeleteAction: Boolean,
        parentLink: String,
    },
    setup(props) {
        const {$i18n} = useTools();

        const headers = [
            {
                text: "...",
                align: "start",
                sortable: false,
                value: "action",
            },
            {
                text: $i18n.t("common.code"),
                align: "start",
                sortable: true,
                value: "code",
            },
            {
                text: $i18n.t("common.label"),
                align: "start",
                sortable: true,
                value: "label",
            },
            {
                text: $i18n.t('pay_section.is_absence_retenue'),
                align: "start",
                sortable: true,
                value: "isAbsenceRetenue",
            },
            {
                text: $i18n.t('pay_section.cotisable'),
                align: "start",
                sortable: true,
                value: "cotisable",
            },
            {
                text: $i18n.t('pay_section.type'),
                align: "start",
                sortable: true,
                value: "type",
            },
            {
                text: "Type Temps",
                align: "start",
                sortable: true,
                value: "variable",
            },
            {
                text: "Imposable",
                align: "start",
                sortable: true,
                value: "imposable",
            },
            {
                text: "Formule",
                align: "start",
                sortable: true,
                value: "formule",
            },
            {
                text: "Brute",
                align: "start",
                sortable: true,
                value: "brute",
            },
            {
                text: "Congé",
                align: "start",
                sortable: true,
                value: "conge",
            },
            {
                text: "Régule",
                align: "start",
                sortable: true,
                value: "regularization",
            },
            {
                text: "Impr. recap",
                align: "start",
                sortable: true,
                value: "recapPrintable",
            },
            {
                text: "Impr. colonne",
                align: "start",
                sortable: true,
                value: "bulteinePrintable",
            },
            {
                text: "Impr. journale",
                align: "start",
                sortable: true,
                value: "journalPrintable",
            },
            {
                text: "Impr. etat des charges",
                align: "start",
                sortable: true,
                value: "stateChargePrintable",
            },
            {
                text: "Ignorer la formule",
                align: "start",
                sortable: true,
                value: "blockFormula",
            },
            {
                text: "Bloquer Saisie",
                align: "start",
                sortable: true,
                value: "notWritable",
            },
            {
                text: "Affichage (Format)",
                align: "start",
                sortable: true,
                value: "viewFormat",
            },
            {
                text: "Type saisie",
                align: "start",
                sortable: true,
                value: "fixeInitial",
            },
            {
                text: "Valeur Initiale",
                align: "start",
                sortable: true,
                value: "initValue",
            },
            {
                text: "Compte",
                align: "start",
                sortable: true,
                value: "comptaCompte",
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.add"),
                icon: 'mdi-plus-circle-outline',
                color: 'teal',
                access: () => props.showAddAction,
                disabled: () => false,
                func: (item) => {
                    add(item)
                },
            },
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => props.showUpdateAction,
                disabled: () => false,
                func: (item) => {
                    edit(item)
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => props.showDeleteAction,
                disabled: () => false,
                func: (item) => {
                    remove(item);
                },
            },
        ];

        const {$router} = useTools();
        const paySectionStore = usePaySectionStore();

        const {selectedOne, itemColor} = useSharedData();

        function rowColor(item) {
            if (item.code === selectedOne.value.code) {
                return itemColor.value;
            }
        }

        function itemClick(item) {
            selectedOne.value = item;
        }

        function add(item) {
            paySectionStore.setPaySection(item);
            $router.push({name: `${props.parentLink}.add`})
        }

        function edit(item) {
            paySectionStore.setPaySection(item);
            $router.push({name: `${props.parentLink}.edit`, params: {id: item.id}})
        }

        function remove(item) {
            paySectionStore.setPaySection(item);
            $router.push({name: `${props.parentLink}.delete`, params: {id: item.id}})
        }

        return {
            headers,
            actions,
            itemClick,
            rowColor,
            add,
            edit,
            remove,
        }
    }
}

</script>

<template>
    <div>
        <w-data-table
            :headers="headers"
            :actions="actions"
            :loading="loading"
            :search="search"
            :items="paySections"
            identity="code"
            :item-class="rowColor"
            @click:row="itemClick"
        >
            <template v-slot:[`item.code`]="{ item }">
                <w-chip :text="item.code"/>
            </template>
            <template v-slot:[`item.label`]="{ item }">
                <w-inline-text bold :text="item.label"/>
            </template>

            <template v-slot:[`item.isAbsenceRetenue`]="{ item }">
                {{ $t(`pay_section.boolean_${item.isAbsenceRetenue}`) }}
            </template>
            <template v-slot:[`item.cotisable`]="{ item }">
                {{ $t(`pay_section.boolean_${item.cotisable}`) }}
            </template>
            <template v-slot:[`item.type`]="{ item }">
                <w-chip color="teal" :text="$t(`pay_section.type_${item.type}`)"/>
            </template>
            <template v-slot:[`item.variable`]="{ item }">
                <w-inline-text v-if="item.variable" :text="$t(`pay_section.variable_${item.variable}`)"/>
            </template>
            <template v-slot:[`item.imposable`]="{ item }">
                <w-chip outlined :text="$t(`pay_section.imposable_${item.imposable}`)"/>
            </template>
            <template v-slot:[`item.formule`]="{ item }">
                <str-formula :formula="item.formule"/>
            </template>
            <template v-slot:[`item.brute`]="{ item }">
                {{ $t(`pay_section.boolean_${item.brute}`) }}
            </template>
            <template v-slot:[`item.conge`]="{ item }">
                {{ $t(`pay_section.boolean_${item.conge}`) }}
            </template>
            <template v-slot:[`item.regularization`]="{ item }">
                {{ $t(`pay_section.boolean_${item.regularization}`) }}
            </template>
            <template v-slot:[`item.recapPrintable`]="{ item }">
                {{ $t(`pay_section.boolean_${item.recapPrintable}`) }}
            </template>
            <template v-slot:[`item.bulteinePrintable`]="{ item }">
                <w-inline-text :text="$t(`pay_section.bulteine_printable_${item.bulteinePrintable}`)"/>
            </template>
            <template v-slot:[`item.journalPrintable`]="{ item }">
                {{ $t(`pay_section.boolean_${item.journalPrintable}`) }}
            </template>
            <template v-slot:[`item.stateChargePrintable`]="{ item }">
                {{ $t(`pay_section.boolean_${item.stateChargePrintable}`) }}
            </template>
            <template v-slot:[`item.blockFormula`]="{ item }">
                {{ $t(`pay_section.boolean_${item.blockFormula}`) }}
            </template>
            <template v-slot:[`item.notWritable`]="{ item }">
                {{ $t(`pay_section.boolean_${item.notWritable}`) }}
            </template>
            <template v-slot:[`item.viewFormat`]="{ item }">
                <w-inline-text :text="item.viewFormat"/>
            </template>
            <template v-slot:[`item.fixeInitial`]="{ item }">
                <w-inline-text
                    v-if="item.fixeInitial !== '0'"
                    :text="$t(`pay_section.view_format_${item.fixeInitial}`)"
                />
            </template>
            <template v-slot:[`item.initValue`]="{ item }">
                <w-inline-text :text="item.initValue"/>
            </template>
            <template v-slot:[`item.comptaCompte`]="{ item }">
                <w-inline-text :text="item.comptaCompte"/>
            </template>
        </w-data-table>
    </div>
</template>

<style scoped>

</style>